import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { FileText, LogOut } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { api } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { getErrorMessage } from '../../lib/utils';
import { FileActions, fileUrl } from '../workplace/DocumentCenter';

const size = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(2)} MB` : `${Math.max(1, Math.round((b || 0) / 1024))} KB`);
const previewable = (d) => /pdf|^image\//.test(d.content_type || '') || /\.pdf$/i.test(d.file_name || '');

/**
 * New policies stop the app until each is accepted. HR marks a document
 * "must accept" when uploading it; a super-tenant's reach every tenant below
 * it. Platform operators and proxy sessions are never stopped (the server
 * answers them with nothing either). If the check itself fails the app opens:
 * nobody is locked out by an outage.
 */
const PolicyGate = ({ children }) => {
  const { user, logout } = useAuth();
  const [pending, setPending] = useState(null);
  const [index, setIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const [src, setSrc] = useState(null);
  const skip = !user || user.isSuperadmin || user.isProxySession;
  const who = user?.email;

  useEffect(() => {
    if (skip) { setPending([]); return undefined; }
    let live = true;
    setPending(null);
    setIndex(0);
    api.get('/api/org-documents/pending-acceptance')
      .then(({ data }) => live && setPending(data?.documents || []))
      .catch(() => live && setPending([]));
    return () => { live = false; };
  }, [skip, who]);

  const doc = pending?.[index];
  // The file is fetched through the API client: an iframe pointed at /api/...
  // on the app's own host would show the app, not the policy.
  useEffect(() => {
    if (!doc || !previewable(doc)) { setSrc(null); return undefined; }
    let href = null;
    let live = true;
    setSrc(null);
    fileUrl(doc.url).then((u) => { href = u; if (live) setSrc(u); else URL.revokeObjectURL(u); }).catch(() => live && setSrc(''));
    return () => { live = false; if (href) URL.revokeObjectURL(href); };
  }, [doc]);

  if (pending === null) return null;
  if (index >= pending.length) return children;

  const accept = async () => {
    setBusy(true);
    try {
      await api.post(`/api/org-documents/${doc.id}/accept`);
      setIndex((i) => i + 1);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not record your acceptance'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background" data-testid="policy-gate">
      <header className="flex items-center justify-between border-b border-border px-4 py-3 sm:px-8">
        <h1 className="text-xl font-semibold tracking-tight">Review documents</h1>
        <Button variant="ghost" size="sm" onClick={logout}><LogOut className="mr-1 h-4 w-4" />Sign out</Button>
      </header>
      <main className="mx-auto flex w-full max-w-4xl flex-1 flex-col gap-3 px-4 py-4 sm:px-8">
        <div>
          <h2 className="text-lg font-semibold text-primary">{doc.title}</h2>
          {doc.description && <p className="text-sm text-muted-foreground">{doc.description}</p>}
          <div className="mt-1 flex items-center justify-between gap-2 text-sm text-muted-foreground">
            <span>{[doc.from_tenant?.name, size(doc.size_bytes)].filter(Boolean).join(' · ')}</span>
            <Badge variant="soft">Policy</Badge>
          </div>
          <div className="mt-2"><FileActions url={doc.url} name={doc.file_name} title={doc.title} /></div>
        </div>
        {previewable(doc) && src ? (
          /^image\//.test(doc.content_type || '')
            ? <img src={src} alt={doc.title} className="w-full rounded-lg border border-border object-contain" />
            : <iframe title={doc.title} src={src} className="min-h-[60vh] w-full flex-1 rounded-lg border border-border bg-muted/30" />
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 rounded-lg border border-border p-8 text-center">
            <FileText className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {previewable(doc) && src === null ? 'Loading the policy…' : 'This file cannot be shown here. Preview or download it, read it, then accept.'}
            </p>
          </div>
        )}
      </main>
      <footer className="sticky bottom-0 flex items-center justify-between border-t border-border bg-background px-4 py-3 sm:px-8">
        <span className="tabular-nums text-muted-foreground">{index + 1}/{pending.length}</span>
        <Button size="lg" disabled={busy} onClick={accept}>{busy ? 'Saving…' : 'Accept policy'}</Button>
      </footer>
    </div>
  );
};

export default PolicyGate;
